import { envConfig } from "Constant";
import { getAccessToken, refreshAccessToken } from "providers/auth-tokens";

interface CoachFetchOptions {
  method?: "GET" | "POST";
  // JSON-serialized when present; omitted entirely (no body, unchanged
  // behavior) when absent.
  body?: unknown;
}

export class CoachAuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CoachAuthError";
  }
}

function coachHeaders(token: string | null | undefined): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
    "X-Platform-Env": envConfig.PLATFORM_ENV ?? "",
    "Content-Type": "application/json",
  };
}

// Coach answers 204 with no body when a write leaves no record behind, and response.json() cannot parse that.
function coachBody<T>(response: Response): Promise<T> {
  return response.status === 204 ? Promise.resolve(null as T) : response.json();
}

export async function coachFetch<T>(
  path: string,
  { method = "GET", body }: CoachFetchOptions = {}
): Promise<T> {
  const url = `${envConfig.COACH_URL}${path}`;
  const requestInit = (token: string | null | undefined): RequestInit => ({
    method,
    headers: coachHeaders(token),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  // Read the live token per request (mirrors Apollo's authLink), not a stale capture.
  const response = await fetch(url, requestInit(getAccessToken()));

  if (response.status === 401) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      throw new CoachAuthError(`Coach ${path} unauthorized — session expired`);
    }
    const retry = await fetch(url, requestInit(refreshed));
    if (retry.status === 401) {
      throw new CoachAuthError(`Coach ${path} unauthorized — session expired`);
    }
    if (!retry.ok) {
      throw new Error(`Coach ${path} failed with status ${retry.status}`);
    }
    return coachBody<T>(retry);
  }

  if (!response.ok) {
    throw new Error(`Coach ${path} failed with status ${response.status}`);
  }

  return coachBody<T>(response);
}

// Tells Coach the learner has Ori's chat on screen; no body, Coach scopes it.
export async function reportChatViewed(): Promise<void> {
  await coachFetch<null>("/api/chat/viewed/", { method: "POST" });
}
